import type { SB } from '@play-co/replicant';
import type { preEntryMessageSchema } from './preEntry.scheduledActions';
import type { preEntrySteps } from './preEntry.ruleset';
import { generateReplicantChatbotPayload } from './messageTemplates';

/**
 * Build attribution shared by scheduling, delivery, and entry links.
 * @param content Message choice from the scheduled-action arguments.
 * @param step Sequence step.
 * @param locale Recipient language, used to identify the D7 artwork.
 * @returns Standard chatbot payload, including the actual creative identifier.
 */
export function getPreEntryPayload(
  content: SB.ExtractType<typeof preEntryMessageSchema>['content'],
  step: (typeof preEntrySteps)[number],
  locale: 'en' | 'ja',
) {
  /** Analytics labels with the agreed snake_case prefix, independent of scheduler notification IDs. */
  const subFeatureByStep: Record<(typeof preEntrySteps)[number], string> = {
    d1_morning: 'pre_entry_d1_AM',
    d1_evening: 'pre_entry_d1_PM',
    d2_morning: 'pre_entry_d2_morning',
    d2_lunch: 'pre_entry_d2_lunch',
    d2_evening: 'pre_entry_d2_dinner',
    d3_reengagement: 'pre_entry_d3_retention',
  };

  // Identify the existing content without reading any pet state.
  let creative: string;
  switch (content.kind) {
    case 'invite':
      creative = content.creative;
      break;
    case 'greeting':
      creative = content.messageId;
      break;
    case 'reengagement':
      creative = `background/reengagement/d7/${locale}`;
      break;
  }

  // Preserve Replicant's success analytics and lastChatbot attribution.
  return {
    ...generateReplicantChatbotPayload({
      feature: 'pre_entry',
      $subFeature: subFeatureByStep[step],
    }),
    $creativeAssetID: creative,
  };
}
